/**
 * Atomic state manager for shared sequence handling
 * Prevents race conditions and ensures consistent state across the application
 */

type SharedSequenceState = {
  isActive: boolean;
  sequenceId: string | null;
  isProcessing: boolean;
  hasProcessed: boolean;
};

class SharedSequenceStateManager {
  private static instance: SharedSequenceStateManager;
  private state: SharedSequenceState = {
    isActive: false,
    sequenceId: null,
    isProcessing: false,
    hasProcessed: false
  };
  
  private listeners: Set<(state: SharedSequenceState) => void> = new Set();
  private processingPromise: Promise<void> | null = null;

  static getInstance(): SharedSequenceStateManager {
    if (!SharedSequenceStateManager.instance) {
      SharedSequenceStateManager.instance = new SharedSequenceStateManager();
    }
    return SharedSequenceStateManager.instance;
  }

  /**
   * Atomically processes a shared sequence URL
   * Ensures only one processing operation can happen at a time
   */
  async processSharedSequence(): Promise<boolean> {
    // If already processing, wait for completion
    if (this.processingPromise) {
      await this.processingPromise;
      return this.state.isActive;
    }

    // Start atomic processing
    this.processingPromise = this._doProcessSharedSequence();
    
    try {
      await this.processingPromise;
      return this.state.isActive;
    } finally {
      this.processingPromise = null;
    }
  }

  private async _doProcessSharedSequence(): Promise<void> {
    console.log('🔄 SharedSequenceStateManager: Starting atomic processing');
    
    // Set processing state
    this.setState({
      ...this.state,
      isProcessing: true
    });

    try {
      // Extract sequence ID from URL
      const urlParams = new URLSearchParams(window.location.search);
      const sequenceId = urlParams.get('sequence');
      
      if (!sequenceId) {
        console.log('🔄 No sequence ID found in URL');
        this.setState({
          isActive: false,
          sequenceId: null,
          isProcessing: false,
          hasProcessed: true
        });
        return;
      }

      console.log('🔄 Found shared sequence:', sequenceId);

      // Atomically update state and localStorage
      this.setState({
        isActive: true,
        sequenceId,
        isProcessing: false,
        hasProcessed: true
      });

      // Set localStorage flags atomically
      localStorage.setItem('babblegram_practice_mode_use_shared_sequence', 'true');
      localStorage.setItem('babblegram_game_mode', 'practice');
      
      // Clear any conflicting flags
      localStorage.removeItem('babblegram_practice_mode_play_again');
      localStorage.removeItem('babblegram_practice_mode_force_reset');
      localStorage.removeItem('from_welcome_page');

      // Update URL to include practice mode
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('mode', 'practice');
      window.history.replaceState({}, '', newUrl.toString());

      console.log('🔄 Shared sequence processing complete:', {
        sequenceId,
        state: this.state
      });

    } catch (error) {
      console.error('🔄 Error processing shared sequence:', error);
      this.setState({
        isActive: false,
        sequenceId: null,
        isProcessing: false,
        hasProcessed: true
      });
    }
  }

  /**
   * Check if we should handle a shared sequence
   * Returns true if processing is needed
   */
  shouldProcessSharedSequence(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    const hasSequenceInUrl = !!urlParams.get('sequence');
    const currentMode = localStorage.getItem('babblegram_game_mode');
    
    return hasSequenceInUrl && 
           !this.state.hasProcessed && 
           !this.state.isProcessing &&
           currentMode !== 'practice';
  }

  /**
   * Check if shared sequence is currently active
   */
  isSharedSequenceActive(): boolean {
    // Check both state and localStorage for consistency
    const urlParams = new URLSearchParams(window.location.search);
    const hasSequenceInUrl = !!urlParams.get('sequence');
    const useSharedFlag = localStorage.getItem('babblegram_practice_mode_use_shared_sequence') === 'true';
    
    return this.state.isActive || (hasSequenceInUrl && useSharedFlag);
  }

  /**
   * Get current sequence ID
   */
  getSequenceId(): string | null {
    if (this.state.sequenceId) {
      return this.state.sequenceId;
    }
    
    // Fallback to URL if state not set yet
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('sequence');
  }

  /**
   * Clear shared sequence state
   */
  clearSharedSequence(): void {
    console.log('🔄 Clearing shared sequence state');
    
    this.setState({
      isActive: false,
      sequenceId: null,
      isProcessing: false,
      hasProcessed: false
    });

    // Clear localStorage flags
    localStorage.removeItem('babblegram_practice_mode_use_shared_sequence');
    
    // Remove sequence from URL
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('sequence');
    window.history.replaceState({}, '', newUrl.toString());
  }

  /**
   * Subscribe to state changes
   */
  subscribe(listener: (state: SharedSequenceState) => void): () => void {
    this.listeners.add(listener);
    // Immediately call with current state
    listener(this.state);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current state
   */
  getState(): SharedSequenceState {
    return { ...this.state };
  }

  private setState(newState: SharedSequenceState): void {
    this.state = newState;
    this.listeners.forEach(listener => listener(this.state));
  }

  /**
   * Reset manager (for testing or cleanup)
   */
  reset(): void {
    this.setState({
      isActive: false,
      sequenceId: null,
      isProcessing: false,
      hasProcessed: false
    });
    this.processingPromise = null;
    this.listeners.clear();
  }
}

export const sharedSequenceStateManager = SharedSequenceStateManager.getInstance();